import { useState } from "react";
import { postJson } from "../api";
import { formatText, useTexts } from "../texts";
import { Notice, TextField, useToast } from "../ui/kit";
import { errorText, type AiError } from "./ai";
import { useIsMobile, Window } from "./widgets";
import styles from "./ai.module.css";

/** La vía (un modelo en un proveedor) cuyo precio se cambia, con su precio vigente. */
export type PricedPath = {
  providerModelId: number;
  providerName: string;
  modelName: string;
  inputPrice: number | null;
  cachedPrice: number | null;
  outputPrice: number | null;
};

type Field = "inputPrice" | "cachedPrice" | "outputPrice";
type Values = Record<Field, string>;

const fields: Field[] = ["inputPrice", "cachedPrice", "outputPrice"];

/** Lo escrito como número (coma o punto decimal); vacío, null; algo que no es un número ≥ 0, NaN. */
export const parsePrice = (value: string) => {
  const text = value.trim().replace(",", ".");
  if (text === "") return null;
  return /^\d+(\.\d+)?$/.test(text) ? Number(text) : Number.NaN;
};

/** El precio vigente tal cual se escribe en el campo («0.028», no «US$ 0.03»). */
const asText = (value: number | null) => (value == null ? "" : String(value));

/**
 * C30 · «Cambiar precio» de un modelo en un proveedor (R14, C24 1.14.11): entrada, entrada en caché y salida por 1M de
 * tokens, en ese orden. Abre con los valores vigentes. Al guardar, cada error va debajo de su campo y se va al corregirlo
 * (C05 1.14.3); sin entrada ni salida, el aviso va debajo de «Precio de entrada». AI Management cierra la vigencia actual y
 * abre la nueva desde ahora (el precio anterior queda en su historia); con los mismos montos no se crea vigencia, por eso
 * sin cambios la ventana solo se cierra. Con cambios sin guardar, × y Esc piden confirmación (C12). No es destructivo:
 * guardar no pide confirmación.
 */
export function ChangePriceWindow({
  path,
  onClose,
  onSaved,
}: {
  path: PricedPath;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const mobile = useIsMobile();
  const initial: Values = {
    inputPrice: asText(path.inputPrice),
    cachedPrice: asText(path.cachedPrice),
    outputPrice: asText(path.outputPrice),
  };
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [discard, setDiscard] = useState(false);
  const dirty = fields.some((field) => values[field].trim() !== initial[field]);

  /** Los errores de lo escrito ahora: la misma regla al guardar y al corregir un campo. */
  const check = (next: Values) => {
    const found: Partial<Record<Field, string>> = {};
    for (const field of fields) if (Number.isNaN(parsePrice(next[field]))) found[field] = L("AI_PRICE_INVALID");
    if (!found.inputPrice && parsePrice(next.inputPrice) == null && parsePrice(next.outputPrice) == null)
      found.inputPrice = L("ADM_AI_PRICE_NEED_ONE");
    return found;
  };
  /** Foco en el primer campo con error (E5). */
  const focusFirst = (found: Partial<Record<Field, string>>) => {
    const first = fields.find((field) => found[field]);
    if (first) requestAnimationFrame(() => document.getElementById(`price-${first}`)?.focus());
  };
  const change = (field: Field, value: string) => {
    const next = { ...values, [field]: value };
    setValues(next);
    // E81: un error marcado se va en cuanto lo escrito es válido; no aparecen errores nuevos mientras se escribe.
    if (Object.keys(errors).length > 0) {
      const now = check(next);
      setErrors(
        (previous) =>
          Object.fromEntries(Object.entries(previous).filter(([key]) => now[key as Field])) as typeof previous,
      );
    }
  };
  const close = () => (dirty && !busy ? setDiscard(true) : onClose());
  const save = async () => {
    if (busy) return;
    if (!dirty) {
      onClose();
      return;
    }
    const found = check(values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      focusFirst(found);
      return;
    }
    setBusy(true);
    setGeneral(null);
    type Reply = { saved?: boolean; fields?: { field: Field; messageKey: string }[] } & AiError;
    // Sin red, un error general (nunca la ventana trabada en «Guardar» cargando).
    const reply = await postJson<Reply>(`/api/admin/ai/provider-models/${path.providerModelId}/price`, {
      inputPrice: parsePrice(values.inputPrice),
      cachedPrice: parsePrice(values.cachedPrice),
      outputPrice: parsePrice(values.outputPrice),
    }).catch(() => ({ status: 0, body: null as Reply | null }));
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      toast.show(formatText(L("ADM_AI_PRICE_SAVED"), path.modelName, path.providerName));
      onSaved();
      return;
    }
    if (reply.body?.fields?.length) {
      const server = Object.fromEntries(reply.body.fields.map((item) => [item.field, L(item.messageKey)]));
      setErrors(server);
      focusFirst(server);
    } else setGeneral(errorText(L, reply.body));
  };

  return (
    <>
      <Window
        open
        title={L("ADM_AI_CHANGE_PRICE")}
        className={styles.priceWindow}
        onClose={close}
        footer={
          <>
            {/* Celular: una ventana sin pasos lleva solo «Guardar»; ← cierra (C30, C21 1.14.12). */}
            {mobile ? null : (
              <button type="button" className="tm-btn secondary" onClick={close} disabled={busy}>
                {L("ACTION_CANCEL")}
              </button>
            )}
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => void save()}
            >
              {L("ADM_AI_SAVE")}
            </button>
          </>
        }
      >
        <form
          className={styles.windowBlock}
          data-part="ventana"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <p className={styles.priceWindowLine}>
            {formatText(L("ADM_AI_CHANGE_PRICE_LINE"), path.modelName, path.providerName)}
          </p>
          {general ? <Notice tone="error">{general}</Notice> : null}
          <TextField
            id="price-inputPrice"
            field="inputPrice"
            label={L("ADM_AI_INPUT_PRICE")}
            value={values.inputPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.inputPrice}
            onChange={(value) => change("inputPrice", value)}
          />
          <TextField
            id="price-cachedPrice"
            field="cachedPrice"
            label={L("ADM_AI_CACHED_PRICE")}
            help={L("ADM_AI_CACHED_PRICE_HELP")}
            value={values.cachedPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.cachedPrice}
            onChange={(value) => change("cachedPrice", value)}
          />
          <TextField
            id="price-outputPrice"
            field="outputPrice"
            label={L("ADM_AI_OUTPUT_PRICE")}
            value={values.outputPrice}
            maxLength={20}
            placeholder={L("ADM_AI_PRICE_HINT")}
            error={errors.outputPrice}
            onChange={(value) => change("outputPrice", value)}
          />
          <p className={styles.priceWindowNote}>{L("ADM_AI_PRICE_FROM_NOW")}</p>
          {/* Enter en un campo guarda (un formulario sin botón de envío propio). */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      </Window>
      <Window
        open={discard}
        confirm
        title={L("CFG1_R1_DISCARD_TITLE")}
        onClose={() => setDiscard(false)}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={() => setDiscard(false)}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              onClick={() => {
                setDiscard(false);
                onClose();
              }}
            >
              {L("CFG1_R1_DISCARD")}
            </button>
          </>
        }
      >
        {null}
      </Window>
    </>
  );
}
